/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { switchMap, map, catchError, of, filter } from "rxjs";
import { Epic } from "redux-observable";

import { Action, ActionType } from "lib/ActionType";

import ServicesContext from "services/ServicesContext";

import * as slice from "./users.slice";


type ActionsType = ActionType<typeof slice.actions>;
type TEpic<I extends Action<any> = any, O extends I & ActionsType = any> = Epic<I, O, { companies: slice.UsersState }, ServicesContext>;


const loadUsers: TEpic<ActionType<typeof slice.actions.loadUsers>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadUsers.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.listUsers(action.payload.companyId).pipe(
            map((users) => slice.actions.usersLoaded({ users })),
            catchError(err => of(slice.actions.usersLoadingError({ err })))
         )
      )
   )

}


const loadUser: TEpic<ActionType<typeof slice.actions.loadUser>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.loadUser.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.readUser(action.payload.companyId, action.payload.id).pipe(
            map((user) => slice.actions.userLoaded({ user })),
            catchError(err => of(slice.actions.userLoadingError({ companyId: action.payload.companyId, id: action.payload.id, err })))
         )
      )

   )

}


const createUser: TEpic<ActionType<typeof slice.actions.createUser>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.createUser.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.createUser(action.payload.user).pipe(
            map((user) => slice.actions.userCreated({ user })),
            catchError(err => of(slice.actions.userCreatingError({ user: action.payload.user, err })))
         )
      )

   )

}


const updateUser: TEpic<ActionType<typeof slice.actions.updateUser>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.updateUser.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.updateUser(action.payload.user).pipe(
            map((user) => slice.actions.userUpdated({ user })),
            catchError(err => of(slice.actions.userUpdateError({ user: action.payload.user, err })))
         )
      )

   )

}


const deleteUser: TEpic<ActionType<typeof slice.actions.deleteUser>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.deleteUser.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.deleteUser(action.payload.user.companyId, action.payload.user.id).pipe(
            map(() => slice.actions.userDeleted({ user: action.payload.user })),
            catchError(err => of(slice.actions.userDeleteError({ user: action.payload.user, err })))
         )
      )

   )

}



const inviteUser: TEpic<ActionType<typeof slice.actions.inviteUser>> = (action$, state, deps) => {

   return action$.pipe(

      filter(
         action => action.type === slice.actions.inviteUser.type
      ),
      switchMap(
         action => deps.api.companyDataProvider.inviteUser(action.payload.user.companyId, action.payload.user.id).pipe(
            map((user) => slice.actions.userInvited({ user })),
            catchError(err => of(slice.actions.userInvitationError({ user: action.payload.user, err })))
         )
      )

   )

}


const epics = [
   loadUsers,
   loadUser,
   createUser,
   updateUser,
   deleteUser,
   inviteUser
];

export default epics;